import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  RefreshCw, Loader2, AlertTriangle, CheckCircle, Clock, Users, 
  FileText, Mail, Calendar, Target, ArrowRight, ExternalLink,
  Briefcase, UserCheck, AlertCircle, XCircle, ShieldAlert, Trophy,
  ChevronLeft, ChevronRight, Medal
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyDeskPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [actionQueue, setActionQueue] = useState({ items: [], total: 0 });
  const [pipelineBoard, setPipelineBoard] = useState({ rows: [] });
  const [missingItems, setMissingItems] = useState({ items: [] });
  const [dailyTargets, setDailyTargets] = useState(null);
  const [overdueItems, setOverdueItems] = useState({ items: [], has_overdue: false });
  const [leaderboard, setLeaderboard] = useState({ leaderboard: [], total: 0, current_user_rank: null });
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('today');
  const [leaderboardPage, setLeaderboardPage] = useState(1);
  // My Desk v2 (2026-09-06): today's tasks, interviews, submissions awaiting a client reply, follow-ups due.
  // Served by routes/my_desk_v2.py, which reads the field names the ATS actually writes.
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const loadLeaderboard = useCallback(async (period = leaderboardPeriod, page = 1) => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(
        `${API_URL}/api/my-desk/v2/leaderboard?period=${period}&page=${page}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data);
        setLeaderboardPage(page);
      }
    } catch (e) {
      console.error('Failed to load leaderboard:', e);
    }
  }, [leaderboardPeriod]);

  const loadData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = localStorage.getItem('ats_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [queueRes, pipelineRes, missingRes, targetsRes, overdueRes, todayRes] = await Promise.all([
        fetch(`${API_URL}/api/my-desk/v2/action-queue`, { headers }),
        fetch(`${API_URL}/api/my-desk/v2/pipeline-board`, { headers }),
        fetch(`${API_URL}/api/my-desk/v2/missing-items`, { headers }),
        fetch(`${API_URL}/api/my-desk/v2/daily-targets`, { headers }),
        fetch(`${API_URL}/api/my-desk/v2/overdue-items`, { headers }),
        fetch(`${API_URL}/api/my-desk/v2/today`, { headers }),
      ]);
      
      if (todayRes.ok) setToday(await todayRes.json());
      if (queueRes.ok) setActionQueue(await queueRes.json());
      if (pipelineRes.ok) setPipelineBoard(await pipelineRes.json());
      if (missingRes.ok) setMissingItems(await missingRes.json());
      if (targetsRes.ok) setDailyTargets(await targetsRes.json());
      if (overdueRes.ok) {
        const overdueData = await overdueRes.json();
        setOverdueItems(overdueData);
        // Reset banner dismissed state if new overdue items appear
        if (overdueData.has_overdue) {
          setBannerDismissed(false);
        }
      }
      
      // Load leaderboard
      await loadLeaderboard();
      
    } catch (e) {
      console.error('Failed to load My Desk data:', e);
      toast.error('Failed to load data');
    }
    
    setLoading(false);
    setRefreshing(false);
  }, [loadLeaderboard]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getActionIcon = (type) => {
    switch (type) {
      case 'source_candidates': return <Users className="w-4 h-4" />;
      case 'screen_candidate': return <UserCheck className="w-4 h-4" />;
      case 'generate_dossier': return <FileText className="w-4 h-4" />;
      case 'submit_candidate': return <Mail className="w-4 h-4" />;
      case 'confirm_interview': return <Calendar className="w-4 h-4" />;
      case 'record_outcome': return <CheckCircle className="w-4 h-4" />;
      case 'overdue_task': return <AlertTriangle className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  const getActionColor = (type, isOverdue) => {
    if (isOverdue) return 'text-red-600 bg-red-50 border-red-200';
    switch (type) {
      case 'source_candidates': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'screen_candidate': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'generate_dossier': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'submit_candidate': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'confirm_interview': return 'text-cyan-600 bg-cyan-50 border-cyan-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  // One vocabulary everywhere: the Requirements page's STAGE_CONFIG (RequirementDetailPage.js).
  const STAGE_LABELS = {
    uploaded: 'Uploaded',
    screened: 'Screened',
    shortlisted: 'Shortlisted',
    lined_up: 'Lined Up',
    interviewed: 'Interviewed',
    offered: 'Offered',
    joined: 'Joined',
    rejected: 'Rejected',
  };

  const STAGE_ORDER = ['uploaded', 'screened', 'shortlisted', 'lined_up', 'interviewed', 'offered', 'joined', 'rejected'];

  const fmtWhen = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const TodayList = ({ title, icon, count, empty, children }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          {icon}
          {title}
          <Badge variant={count > 0 ? 'secondary' : 'outline'} className="ml-auto">{count}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {count === 0 ? (
          <p className="text-xs text-slate-400 py-2">{empty}</p>
        ) : (
          <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1 max-md:[&_a]:min-h-[44px] max-md:[&_a]:items-center">{children}</ul>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header title="My Desk" subtitle="Your recruiting pipeline at a glance" />
        <main className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="My Desk" subtitle="Your recruiting pipeline at a glance">
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadData(true)}
          disabled={refreshing}
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </Header>
      
      <main className="max-w-7xl mx-auto px-4 py-6 max-md:pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        {/* OVERDUE LOCK BANNER - Red banner when items are overdue > 24h */}
        {overdueItems.has_overdue && !bannerDismissed && (
          <div 
            className="mb-6 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg p-4 shadow-lg animate-pulse"
            data-testid="overdue-lock-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-full">
                  <ShieldAlert className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">🚨 Action Required: {overdueItems.items.length} Overdue Item{overdueItems.items.length !== 1 ? 's' : ''}</h3>
                  <p className="text-red-100 text-sm">
                    These items have been pending for over 24 hours. Clear them to maintain pipeline discipline.
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBannerDismissed(true)}
                className="text-white hover:bg-white/20 max-md:min-h-[44px] max-md:min-w-[44px]"
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {overdueItems.items.slice(0, 5).map((item, idx) => (
                <Link
                  key={idx}
                  to={item.link}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-sm transition-colors"
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {item.title}
                </Link>
              ))}
              {overdueItems.items.length > 5 && (
                <span className="inline-flex items-center px-3 py-1.5 bg-white/10 rounded-full text-sm">
                  +{overdueItems.items.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Daily Targets Strip */}
        {dailyTargets && (
          <Card className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-emerald-800">Today&apos;s Progress</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-700">
                      {dailyTargets.cvs.actual}/{dailyTargets.cvs.target}
                    </div>
                    <div className="text-xs text-emerald-600">CVs Uploaded</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-700">
                      {dailyTargets.submissions.actual}/{dailyTargets.submissions.target}
                    </div>
                    <div className="text-xs text-emerald-600">Submissions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-700">
                      {dailyTargets.followups.actual}/{dailyTargets.followups.target}
                    </div>
                    <div className="text-xs text-emerald-600">Follow-ups</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TODAY - the work that is actually waiting (My Desk v2) */}
        {today && (
          <div className="mb-6" data-testid="my-desk-today">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-600" />
                Today
                <span className="text-xs font-normal text-slate-500">
                  {today.scope === 'all' ? 'all recruiters' : 'your work'} · {today.date}
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <TodayList
                title="Tasks"
                icon={<CheckCircle className="w-4 h-4 text-emerald-500" />}
                count={today.tasks_today.length + today.tasks_overdue.length}
                empty="No tasks due today"
              >
                {today.tasks_overdue.map((t) => (
                  <li key={`${t.source}-${t.id}`}>
                    <Link to={t.link} className="flex items-start gap-2 text-sm hover:text-emerald-700">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-red-500 flex-shrink-0" />
                      <span className="truncate flex-1">{t.title}</span>
                      <span className="text-xs text-red-500 flex-shrink-0">overdue {String(t.due_date || '').slice(0, 10)}</span>
                    </Link>
                  </li>
                ))}
                {today.tasks_today.map((t) => (
                  <li key={`${t.source}-${t.id}`}>
                    <Link to={t.link} className="flex items-start gap-2 text-sm hover:text-emerald-700">
                      <Clock className="w-3.5 h-3.5 mt-0.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate flex-1">{t.title}</span>
                      <span className="text-xs text-slate-400 flex-shrink-0">{t.due_date ? 'today' : 'no date'}</span>
                    </Link>
                  </li>
                ))}
              </TodayList>

              <TodayList
                title="Interviews"
                icon={<Calendar className="w-4 h-4 text-cyan-500" />}
                count={today.interviews.length}
                empty="Nothing scheduled today or tomorrow"
              >
                {today.interviews.map((iv) => (
                  <li key={iv.id}>
                    <Link to={iv.link} className="flex items-start gap-2 text-sm hover:text-cyan-700">
                      <span className={cn('text-xs px-1.5 rounded flex-shrink-0 mt-0.5', iv.is_today ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-500')}>
                        {iv.is_today ? 'today' : 'tmrw'}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate font-medium">{iv.candidate_name}</span>
                        <span className="block truncate text-xs text-slate-500">
                          {[iv.position_title, iv.client_name].filter(Boolean).join(' · ')} — {fmtWhen(iv.when)} IST
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </TodayList>

              <TodayList
                title="Awaiting client reply"
                icon={<Mail className="w-4 h-4 text-amber-500" />}
                count={today.counts.submissions_awaiting}
                empty="No submissions waiting on a client"
              >
                {today.submissions_awaiting.map((s) => (
                  <li key={`${s.source}-${s.id}`}>
                    <Link to={s.link} className="flex items-start gap-2 text-sm hover:text-amber-700">
                      <span className="flex-1 min-w-0">
                        <span className="block truncate font-medium">{s.client_name || s.to_email || 'Client'}</span>
                        <span className="block truncate text-xs text-slate-500">
                          {s.position_title || 'Submission'} · {s.candidates} CV{s.candidates === 1 ? '' : 's'}
                          {s.opened ? ' · opened' : ' · not opened'}
                        </span>
                      </span>
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {s.days_waiting != null ? `${s.days_waiting}d` : ''}
                      </span>
                    </Link>
                  </li>
                ))}
              </TodayList>

              <TodayList
                title="Follow-ups due"
                icon={<AlertCircle className="w-4 h-4 text-red-500" />}
                count={today.counts.followups_due}
                empty="Nothing to chase"
              >
                {today.followups_due.map((f) => (
                  <li key={`fu-${f.source}-${f.id}`}>
                    <Link to={f.link} className="flex items-start gap-2 text-sm hover:text-red-700">
                      <span className="flex-1 min-w-0">
                        <span className="block truncate font-medium">{f.client_name || f.to_email || 'Client'}</span>
                        <span className="block truncate text-xs text-slate-500">{f.position_title || 'Submission'} · {f.reason}</span>
                      </span>
                      <span className="text-xs text-red-500 flex-shrink-0">
                        {f.days_waiting != null ? `${f.days_waiting}d` : ''}
                      </span>
                    </Link>
                  </li>
                ))}
              </TodayList>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Action Queue */}
          <div className="lg:col-span-2 space-y-6">
            {/* Action Queue */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    Action Queue
                    {actionQueue.total > 0 && (
                      <Badge variant="secondary">{actionQueue.total}</Badge>
                    )}
                  </CardTitle>
                  {actionQueue.items.some(i => i.is_overdue) && (
                    <Badge variant="destructive" className="animate-pulse">
                      {actionQueue.items.filter(i => i.is_overdue).length} Overdue
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {actionQueue.items.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                    <p className="font-medium">All caught up!</p>
                    <p className="text-sm">No pending actions in your queue</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {actionQueue.items.map((item, idx) => (
                      <Link
                        key={idx}
                        to={item.link}
                        className={cn(
                          "block p-3 rounded-lg border transition-all hover:shadow-md",
                          getActionColor(item.type, item.is_overdue)
                        )}
                        data-testid={`action-item-${idx}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            "p-2 rounded-lg",
                            item.is_overdue ? "bg-red-100" : "bg-white/50"
                          )}>
                            {getActionIcon(item.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-800 truncate">
                              {item.title}
                            </div>
                            <div className="text-sm text-slate-600 truncate">
                              {item.subtitle}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                        </div>
                      </Link>
                    ))}
                    {actionQueue.total > 10 && (
                      <div className="text-center text-sm text-slate-500 pt-2">
                        +{actionQueue.total - 10} more items
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pipeline Board */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-500" />
                  Pipeline Board
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pipelineBoard.rows.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No active requirements assigned to you</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto max-md:hidden">
                    <table className="w-full min-w-[820px]">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase">
                            Requirement
                          </th>
                          {STAGE_ORDER.map(stage => (
                            <th key={stage} className="text-center py-2 px-2 text-xs font-medium text-slate-500 uppercase">
                              {STAGE_LABELS[stage]}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pipelineBoard.rows.map((row, idx) => (
                          <tr key={idx} className="border-b last:border-b-0 hover:bg-slate-50">
                            <td className="py-3 px-3">
                              <Link 
                                to={`/ats/requirements/${row.requirement_id}`}
                                className="hover:text-emerald-600"
                              >
                                <div className="font-medium text-slate-800 truncate max-w-[200px]">
                                  {row.position_title}
                                </div>
                                <div className="text-xs text-slate-500">{row.client_name}</div>
                              </Link>
                            </td>
                            {STAGE_ORDER.map(stage => {
                              const count = row.stages[stage] || 0;
                              const hasBreach = row.sla_breaches?.[stage] > 0;
                              return (
                                <td key={stage} className="text-center py-3 px-2">
                                  <div className={cn(
                                    "inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
                                    count > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400",
                                    hasBreach && "ring-2 ring-red-400 ring-offset-1"
                                  )}>
                                    {count}
                                  </div>
                                  {hasBreach && (
                                    <div className="text-xs text-red-500 mt-1">
                                      {row.sla_breaches[stage]} overdue
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Phones: the same pipeline rows as stacked cards, so the board
                    never forces the page sideways. */}
                {pipelineBoard.rows.length > 0 && (
                  <div className="md:hidden divide-y divide-slate-100">
                    {pipelineBoard.rows.map((row, idx) => (
                      <div key={`m-${idx}`} className="py-3 space-y-2" data-testid={`pipeline-card-${idx}`}>
                        <Link to={`/ats/requirements/${row.requirement_id}`} className="block">
                          <div className="font-semibold text-slate-800 break-words">{row.position_title}</div>
                          <div className="text-xs text-slate-500 break-words">{row.client_name}</div>
                        </Link>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                          {STAGE_ORDER.map(stage => {
                            const count = row.stages[stage] || 0;
                            const hasBreach = row.sla_breaches?.[stage] > 0;
                            return (
                              <div key={stage} className="flex items-center gap-2 min-w-0">
                                <span className="text-xs uppercase tracking-wide text-slate-400 truncate flex-1">
                                  {STAGE_LABELS[stage]}
                                </span>
                                <span className={cn(
                                  "inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-full text-sm font-medium shrink-0",
                                  count > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400",
                                  hasBreach && "ring-2 ring-red-400"
                                )}>
                                  {count}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column: What You&apos;re Missing */}
          <div>
            <Card className="sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  What You&apos;re Missing
                </CardTitle>
              </CardHeader>
              <CardContent>
                {missingItems.items.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                    <p className="font-medium">Looking good!</p>
                    <p className="text-sm">No gaps in your pipeline</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {missingItems.items.map((item, idx) => (
                      <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <Link 
                          to={`/ats/requirements/${item.requirement_id}`}
                          className="font-medium text-amber-800 hover:underline flex items-center gap-1"
                        >
                          {item.position_title}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                        <div className="text-xs text-amber-600 mb-2">{item.client_name}</div>
                        <ul className="space-y-1">
                          {item.missing.map((m, mIdx) => (
                            <li key={mIdx} className="text-sm text-amber-700 flex items-start gap-2">
                              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard Widget */}
            <Card className="mt-6" data-testid="leaderboard-widget">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Team Leaderboard
                  </CardTitle>
                  <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
                    <button
                      onClick={() => {
                        setLeaderboardPeriod('today');
                        loadLeaderboard('today', 1);
                      }}
                      className={cn(
                        "px-3 py-1 text-xs font-medium rounded-md transition-colors max-md:min-h-[44px] max-md:flex-1",
                        leaderboardPeriod === 'today' 
                          ? "bg-white text-slate-900 shadow-sm" 
                          : "text-slate-600 hover:text-slate-900"
                      )}
                      data-testid="leaderboard-toggle-today"
                    >
                      Today
                    </button>
                    <button
                      onClick={() => {
                        setLeaderboardPeriod('week');
                        loadLeaderboard('week', 1);
                      }}
                      className={cn(
                        "px-3 py-1 text-xs font-medium rounded-md transition-colors max-md:min-h-[44px] max-md:flex-1",
                        leaderboardPeriod === 'week' 
                          ? "bg-white text-slate-900 shadow-sm" 
                          : "text-slate-600 hover:text-slate-900"
                      )}
                      data-testid="leaderboard-toggle-week"
                    >
                      This Week
                    </button>
                  </div>
                </div>
                {leaderboard.current_user_rank && (
                  <p className="text-xs text-slate-500 mt-1">
                    Your rank: <span className="font-semibold text-amber-600">#{leaderboard.current_user_rank}</span> of {leaderboard.total}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                {leaderboard.leaderboard.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">
                    <Trophy className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No activity yet {leaderboardPeriod === 'today' ? 'today' : 'this week'}</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {leaderboard.leaderboard.map((entry) => (
                        <div 
                          key={entry.id}
                          className={cn(
                            "flex items-center gap-3 p-2.5 rounded-lg transition-colors",
                            entry.is_current_user 
                              ? "bg-amber-50 border border-amber-200" 
                              : "bg-slate-50 hover:bg-slate-100"
                          )}
                        >
                          {/* Rank */}
                          <div className={cn(
                            "w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold",
                            entry.rank === 1 ? "bg-amber-400 text-white" :
                            entry.rank === 2 ? "bg-slate-400 text-white" :
                            entry.rank === 3 ? "bg-orange-400 text-white" :
                            "bg-slate-200 text-slate-600"
                          )}>
                            {entry.rank <= 3 ? (
                              <Medal className="w-4 h-4" />
                            ) : entry.rank}
                          </div>
                          
                          {/* Name */}
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              "text-sm font-medium truncate",
                              entry.is_current_user ? "text-amber-800" : "text-slate-700"
                            )}>
                              {entry.name} {entry.is_current_user && <span className="text-xs">(You)</span>}
                            </p>
                            <div className="flex gap-2 text-xs text-slate-500">
                              <span>{entry.cvs_uploaded} CVs</span>
                              <span>·</span>
                              <span>{entry.submissions} Subs</span>
                              <span>·</span>
                              <span>{entry.followups} FUs</span>
                            </div>
                          </div>
                          
                          {/* Score */}
                          <div className="text-right">
                            <p className={cn(
                              "text-sm font-bold",
                              entry.rank === 1 ? "text-amber-600" : "text-slate-600"
                            )}>
                              {entry.score}
                            </p>
                            <p className="text-xs text-slate-400">pts</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Pagination */}
                    {leaderboard.total_pages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={leaderboardPage <= 1}
                          onClick={() => loadLeaderboard(leaderboardPeriod, leaderboardPage - 1)}
                          className="max-md:min-h-[44px] max-md:min-w-[44px]"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-xs text-slate-500">
                          Page {leaderboardPage} of {leaderboard.total_pages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={leaderboardPage >= leaderboard.total_pages}
                          onClick={() => loadLeaderboard(leaderboardPeriod, leaderboardPage + 1)}
                          className="max-md:min-h-[44px] max-md:min-w-[44px]"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    {/* Scoring Explanation */}
                    <div className="mt-3 p-2 bg-slate-50 rounded-lg text-xs text-slate-500">
                      <p className="font-medium text-slate-600 mb-1">Scoring:</p>
                      <p>1pt per CV · 3pts per Submission · 2pts per Follow-up</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
